import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import BrandMark from "components/BrandMark";
import { useLocationContext } from "context/LocationContext";
import useAuth from "hooks/useAuth";
import { isModuleAllowedForLevel } from "config/contextModules";
import { isSuperuserUser } from "utils/dashboardScope";

function getNodeIconName(node) {
  if (node.level === "national") return "Globe";
  if (node.level === "region") return "MapPin";
  if (node.level === "subdistrict") return "Map";
  return "Building2";
}

function isTestRegionNode(node) {
  if (node?.level !== "region") return false;

  const name = String(node.name || "").trim().toLowerCase();
  const slug = String(node.slug || "").trim().toLowerCase();

  return name === "ao" || name === "ao region" || slug === "ao" || slug === "ao-region";
}

function filterOrgTree(tree, searchTerm) {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) return tree;

  if ("ghana".includes(query)) {
    return tree;
  }

  const filteredRegions = (tree?.children || [])
    .map((region) => {
      const regionMatches = region.name.toLowerCase().includes(query);
      const matchingDistricts = (region.children || [])
        .map((district) => {
          const districtMatches = district.name.toLowerCase().includes(query);
          const matchingSubdistricts = (district.children || []).filter((subdistrict) =>
            subdistrict.name.toLowerCase().includes(query),
          );

          if (districtMatches) {
            return district;
          }

          if (matchingSubdistricts.length > 0) {
            return {
              ...district,
              children: matchingSubdistricts,
            };
          }

          return null;
        })
        .filter(Boolean);

      if (regionMatches) {
        return region;
      }

      if (matchingDistricts.length > 0) {
        return {
          ...region,
          children: matchingDistricts,
        };
      }

      return null;
    })
    .filter(Boolean);

  return {
    ...tree,
    children: filteredRegions,
  };
}

function GeoTreeNode({
  node,
  depth,
  expandedRegionId,
  expandedDistrictIds,
  onToggle,
  onSelect,
  activePathIds,
  currentLocationId,
  searchActive,
}) {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isRoot = node.level === "national";
  const isRegion = node.level === "region";
  const isDistrict = node.level === "district";

  const isExpanded = hasChildren
    ? isRoot ||
      searchActive ||
      (isRegion && expandedRegionId === node.id) ||
      (isDistrict && expandedDistrictIds[node.id])
    : false;
  const isActive = currentLocationId === node.id;
  const isInPath = activePathIds.includes(node.id);
  const isReadonly = node.hasAccess === false;

  return (
    <li className="space-y-0.5">
      <div className="flex items-center gap-0.5 min-w-0">
        {hasChildren && !isRoot ? (
          <button
            type="button"
            onClick={() => onToggle(node)}
            className="h-6 w-6 rounded-md flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 transition-colors shrink-0"
            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${node.name}`}
          >
            <Icon name={isExpanded ? "ChevronDown" : "ChevronRight"} className="w-3.5 h-3.5" />
          </button>
        ) : (
          <span className="h-6 w-6 shrink-0" />
        )}

        <button
          type="button"
          onClick={() => !isReadonly && onSelect(node)}
          aria-current={isActive ? "page" : undefined}
          title={`${isRoot ? "Ghana (National)" : node.name}${isReadonly ? " - view only" : ""}`}
          className={`flex-1 min-w-0 text-left rounded-md px-2.5 py-1.5 border transition-colors ${
            isReadonly
              ? "text-slate-400 border-transparent cursor-not-allowed opacity-75"
              : isActive
              ? "bg-white text-slate-900 border-white shadow-sm"
              : isInPath
                ? "bg-white/10 text-white border-white/10"
                : "text-slate-200 border-transparent hover:text-white hover:bg-white/8 hover:border-white/10"
          }`}
          disabled={isReadonly}
        >
          <span className="flex items-center justify-between gap-1.5 min-w-0">
            <span className="flex items-center gap-1.5 min-w-0">
              <Icon name={getNodeIconName(node)} className="w-4 h-4 shrink-0" />
              <span className="truncate text-[13px] leading-4 font-medium">
                {isRoot ? "Ghana (National)" : node.name}
              </span>
            </span>
            {isReadonly && (
              <span className="inline-flex items-center text-[9px] uppercase tracking-wide text-amber-200 shrink-0">
                <Icon name="Lock" className="w-2.5 h-2.5 mr-0.5" />
                RO
              </span>
            )}
          </span>
        </button>
      </div>

      {hasChildren && isExpanded && (
        <>
          {/* Regular regions (excluding AO Region) */}
          {node.children
            .filter(childNode => childNode.name !== "AO Region")
            .length > 0 && (
            <ul className={`mt-0.5 space-y-0.5 ${depth >= 0 ? "border-l border-white/10 ml-2 pl-1.5" : ""}`}>
              {node.children
                .filter(childNode => childNode.name !== "AO Region")
                .map((childNode) => (
                  <GeoTreeNode
                    key={childNode.id}
                    node={childNode}
                    depth={depth + 1}
                    expandedRegionId={expandedRegionId}
                    expandedDistrictIds={expandedDistrictIds}
                    onToggle={onToggle}
                    onSelect={onSelect}
                    activePathIds={activePathIds}
                    currentLocationId={currentLocationId}
                    searchActive={searchActive}
                  />
                ))}
            </ul>
          )}

          {/* AO Region - separated at the bottom */}
          {node.children.some(childNode => childNode.name === "AO Region") && (
            <div className="mt-4 pt-3 border-t border-white/10">
              <ul className="space-y-0.5">
                {node.children
                  .filter(childNode => childNode.name === "AO Region")
                  .map((childNode) => (
                    <GeoTreeNode
                      key={childNode.id}
                      node={childNode}
                      depth={depth + 1}
                      expandedRegionId={expandedRegionId}
                      expandedDistrictIds={expandedDistrictIds}
                      onToggle={onToggle}
                      onSelect={onSelect}
                      activePathIds={activePathIds}
                      currentLocationId={currentLocationId}
                      searchActive={searchActive}
                    />
                  ))}
              </ul>
            </div>
          )}
        </>
      )}
    </li>
  );
}

export default function GeoSidebar({
  isPinnedOpen = true,
  onPinnedOpenChange,
  isMobileOpen = false,
  onMobileClose,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    orgTree,
    isOrgTreeLoading,
    orgTreeError,
    currentLocation,
    currentModuleSlug,
    activePathIds,
    goToModule,
    selectLocation,
  } = useLocationContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRegionId, setExpandedRegionId] = useState(null);
  const [expandedDistrictIds, setExpandedDistrictIds] = useState({});
  const [isHovering, setIsHovering] = useState(false);
  const [suppressHoverExpand, setSuppressHoverExpand] = useState(false);
  const isExpandedLayout = isMobileOpen || isPinnedOpen || isHovering;
  const canOpenUsersAccess =
    currentLocation &&
    isModuleAllowedForLevel("users-access", currentLocation.level || "national", user);
  const isUsersAccessActive = currentModuleSlug === "users-access";

  const activeRegionId = useMemo(
    () => activePathIds.find((id) => String(id).startsWith("region:")) || null,
    [activePathIds],
  );

  useEffect(() => {
    if (activeRegionId) {
      setExpandedRegionId(activeRegionId);
    }
  }, [activeRegionId]);

  useEffect(() => {
    const activeDistrictId = activePathIds.find((id) => String(id).startsWith("district:"));
    if (activeDistrictId) {
      setExpandedDistrictIds((prev) => ({ ...prev, [activeDistrictId]: true }));
    }
  }, [activePathIds]);

  const filteredTree = useMemo(
    () => filterOrgTree(orgTree, searchTerm),
    [orgTree, searchTerm],
  );
  const officialTree = useMemo(
    () => ({
      ...filteredTree,
      children: (filteredTree?.children || []).filter((region) => !isTestRegionNode(region)),
    }),
    [filteredTree],
  );
  const testRegions = useMemo(
    () => (filteredTree?.children || []).filter(isTestRegionNode),
    [filteredTree],
  );

  const searchActive = Boolean(searchTerm.trim());
  const officialRegionCount = officialTree?.children?.length || 0;
  const testRegionCount = testRegions.length;
  const filteredRegionCount = officialRegionCount + testRegionCount;
  const filteredDistrictCount = useMemo(
    () => (filteredTree?.children || []).reduce((sum, region) => sum + (region.children?.length || 0), 0),
    [filteredTree],
  );
  const filteredSubdistrictCount = useMemo(
    () =>
      (filteredTree?.children || []).reduce(
        (sum, region) =>
          sum +
          (region.children || []).reduce(
            (districtSum, district) => districtSum + (district.children?.length || 0),
            0,
          ),
        0,
      ),
    [filteredTree],
  );

  const handleToggle = (node) => {
    if (node.level === "region") {
      setExpandedRegionId((prev) => (prev === node.id ? null : node.id));
      return;
    }

    if (node.level === "district") {
      setExpandedDistrictIds((prev) => ({
        ...prev,
        [node.id]: !prev[node.id],
      }));
    }
  };

  const handleSelect = (node) => {
    if (node.level === "region") {
      setExpandedRegionId(node.id);
    }
    if (node.level === "district") {
      setExpandedDistrictIds((prev) => ({ ...prev, [node.id]: true }));
    }
    selectLocation(node, currentModuleSlug);
    onMobileClose?.();
  };

  const handlePreviewMouseEnter = () => {
    if (!suppressHoverExpand) {
      setIsHovering(true);
    }
  };

  const handleSidebarMouseLeave = () => {
    setIsHovering(false);
    setSuppressHoverExpand(false);
  };

  const handlePinnedToggle = () => {
    const nextPinnedOpen = !isPinnedOpen;
    onPinnedOpenChange?.(nextPinnedOpen);

    if (!nextPinnedOpen) {
      setIsHovering(false);
      setSuppressHoverExpand(true);
    }
  };

  return (
    <aside
      onMouseLeave={handleSidebarMouseLeave}
      className={`h-screen fixed left-0 top-0 z-50 flex flex-col overflow-hidden text-white shadow-xl border-r border-white/10 transition-[width,transform] duration-200 ease-out max-md:w-72 ${
        isExpandedLayout ? "w-72" : "w-16"
      } ${
        isMobileOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full"
      }`}
      style={{
        background: "linear-gradient(180deg, #16325a 0%, #0d1f3a 100%)",
      }}
    >
      <div
        className={`h-16 border-b border-white/10 bg-white/[0.02] ${
          isExpandedLayout ? "px-5" : "px-2"
        }`}
      >
        <div className={`flex h-full items-center ${isExpandedLayout ? "gap-3" : "justify-center"}`}>
          <BrandMark
            alt="DDDP logo"
            className={`rounded-xl bg-white shadow-md shrink-0 ${
              isExpandedLayout ? "h-10 w-10 p-1.5" : "h-8 w-8 p-1"
            }`}
          />
          <div
            className={`min-w-0 flex-none overflow-hidden transition-opacity duration-100 ease-out ${
              isExpandedLayout
                ? "w-36 opacity-100 delay-100"
                : "w-0 opacity-0 pointer-events-none"
            }`}
            aria-hidden={!isExpandedLayout}
          >
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">DDDP</p>
            <h2 className="text-sm font-semibold leading-5 text-white">
              District Development Data Platform
            </h2>
          </div>
          {isExpandedLayout && (
            <button
              type="button"
              onClick={handlePinnedToggle}
              className="ml-auto hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-200 transition-colors hover:bg-white/10 hover:text-white md:flex"
              title={isPinnedOpen ? "Collapse sidebar" : "Keep sidebar open"}
              aria-label={isPinnedOpen ? "Collapse sidebar" : "Keep sidebar open"}
            >
              <Icon name={isPinnedOpen ? "PanelLeftClose" : "PanelLeftOpen"} className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onMobileClose}
            className="ml-auto hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-200 transition-colors hover:bg-white/10 hover:text-white max-md:flex"
            aria-label="Close location navigation"
          >
            <Icon name="X" className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!isExpandedLayout && !isMobileOpen ? (
        <div className="flex flex-1 flex-col">
          <div
            className="flex flex-1 flex-col items-center gap-3 px-2.5 py-4"
            onMouseEnter={handlePreviewMouseEnter}
          >
            <div
              className="h-9 w-9 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-slate-200"
              title={currentLocation?.name || "Ghana"}
            >
              <Icon name="MapPinned" className="w-4 h-4" />
            </div>
          </div>
          {canOpenUsersAccess && (
            <div className="px-2.5 py-4 border-t border-white/10 bg-white/[0.02]">
              <button
                type="button"
                onClick={() => goToModule("users-access")}
                className={`h-9 w-9 rounded-lg border flex items-center justify-center transition-colors ${
                  isUsersAccessActive
                    ? "border-amber-300/40 bg-amber-300/15 text-amber-100"
                    : "border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white"
                }`}
                title="Users & Access"
                aria-label="Users & Access"
              >
                <Icon name="ShieldCheck" className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
      <div className="px-4 py-4 pr-2 flex-1 overflow-y-auto overflow-x-hidden scrollbar-sidebar">
        <section className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
          <div className="p-3">
            <div className="mb-3">
              {isOrgTreeLoading && (
                <div className="mb-2 text-[11px] text-slate-400 text-right">Loading...</div>
              )}
              <div className="relative">
                <Icon name="Search" className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search region, district, sub-district..."
                  className="w-full rounded-lg border border-white/10 bg-white/5 pl-9 pr-9 py-2 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10"
                    aria-label="Clear search"
                  >
                    <Icon name="X" className="w-4 h-4" />
                  </button>
                )}
              </div>
              {searchActive && (
                <p className="mt-2 text-xs text-slate-400">
                  {filteredRegionCount} region{filteredRegionCount === 1 ? "" : "s"} / {filteredDistrictCount} district{filteredDistrictCount === 1 ? "" : "s"} / {filteredSubdistrictCount} sub-district{filteredSubdistrictCount === 1 ? "" : "s"} matched
                </p>
              )}
            </div>

            {orgTreeError && (
              <div className="mb-3 rounded-md border border-amber-300/20 bg-amber-300/10 px-3 py-2 text-xs text-amber-100">
                {orgTreeError}
              </div>
            )}

            <div className="rounded-xl border border-white/10 bg-black/10 p-2">
              {!filteredTree ? (
                <div className="rounded-md border border-white/10 bg-white/5 px-3 py-3 text-sm text-slate-300">
                  {isOrgTreeLoading ? "Loading locations..." : "Location tree unavailable."}
                </div>
              ) : filteredRegionCount === 0 && searchActive ? (
                <div className="rounded-md border border-white/10 bg-white/5 px-3 py-3 text-sm text-slate-300">
                  No regions or districts matched "{searchTerm}".
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      Regions
                    </p>
                    {officialRegionCount === 0 ? (
                      <div className="rounded-md border border-white/10 bg-white/5 px-3 py-3 text-sm text-slate-300">
                        No official regions matched "{searchTerm}".
                      </div>
                    ) : (
                      <ul className="space-y-1">
                        <GeoTreeNode
                          node={officialTree}
                          depth={0}
                          expandedRegionId={expandedRegionId}
                          expandedDistrictIds={expandedDistrictIds}
                          onToggle={handleToggle}
                          onSelect={handleSelect}
                          activePathIds={activePathIds}
                          currentLocationId={currentLocation?.id}
                          searchActive={searchActive}
                        />
                      </ul>
                    )}
                  </div>

                  {testRegionCount > 0 && (
                    <div className="border-t border-white/10 pt-3">
                      <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200">
                        Test Region
                      </p>
                      <ul className="space-y-1 rounded-lg border border-amber-200/20 bg-amber-200/5 p-1.5">
                        {testRegions.map((testRegion) => (
                          <GeoTreeNode
                            key={testRegion.id}
                            node={testRegion}
                            depth={0}
                            expandedRegionId={expandedRegionId}
                            expandedDistrictIds={expandedDistrictIds}
                            onToggle={handleToggle}
                            onSelect={handleSelect}
                            activePathIds={activePathIds}
                            currentLocationId={currentLocation?.id}
                            searchActive={searchActive}
                          />
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
      )}

      {isExpandedLayout && (
      <div className="px-4 py-4 border-t border-white/10 bg-white/[0.02] space-y-3">
        <div className="space-y-1.5 rounded-lg border border-white/10 bg-black/10 p-2">
          <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            Context
          </p>
          <div className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-slate-200">
            <span className="h-7 w-7 rounded-md bg-white/10 flex items-center justify-center shrink-0">
              <Icon name="MapPinned" className="w-3.5 h-3.5 text-slate-200" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium" title={currentLocation?.name || "Ghana"}>
                {currentLocation?.name || "Ghana"}
              </span>
              <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-slate-400">
                {currentLocation?.level || "national"}
                {currentLocation?.level === "district" && !currentLocation?.orgUnitId && (
                  <Icon name="AlertCircle" className="w-3 h-3 text-amber-200" />
                )}
              </span>
            </span>
          </div>
        </div>

        {isSuperuserUser(user) && (
          <div className="space-y-1.5 rounded-lg border border-white/10 bg-black/10 p-2">
            <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              Administration
            </p>
            {canOpenUsersAccess && (
              <button
                type="button"
                onClick={() => goToModule("users-access")}
                className={`w-full rounded-md px-2.5 py-2 flex items-center justify-between gap-2 text-left transition-colors ${
                  isUsersAccessActive
                    ? "bg-amber-300/15 text-white"
                    : "text-slate-200 hover:bg-white/10 hover:text-white"
                }`}
              >
                <span className="flex items-center gap-2.5 min-w-0 flex-1">
                  <span className="h-7 w-7 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                    <Icon name="ShieldCheck" className="w-3.5 h-3.5 text-slate-200" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">Users & Access</span>
                </span>
                <Icon name="ChevronRight" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate("/setup")}
              className="w-full rounded-md px-2.5 py-2 flex items-center justify-between gap-2 text-left text-slate-200 hover:bg-white/10 hover:text-white transition-colors"
            >
              <span className="flex items-center gap-2.5 min-w-0 flex-1">
                <span className="h-7 w-7 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                  <Icon name="SlidersHorizontal" className="w-3.5 h-3.5 text-slate-200" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">Setup Mode</span>
              </span>
              <Icon name="ChevronRight" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            </button>
          </div>
        )}
      </div>
      )}
    </aside>
  );
}
// Number of projects completed, No. of rollover projects,//
// Activity plan: plan executed, 
// Meeting: 

// Projects: by dimension, sector, meeting type
// Revenue tracker graph
// Budget allocation tracker chart
